import { z } from 'zod';

/**
 * Files: metadata plus an upload/download handshake around object storage,
 * folders ("Mi Nube" — each user's own drive), and share links.
 *
 * Bytes never pass through a JSON body. `POST /files/upload-sessions`
 * returns a signed URL the browser uploads directly to (the storage
 * provider, or — for local development — a raw byte endpoint this service
 * exposes itself); `POST .../complete` then verifies the object actually
 * landed before a `FileObject` row exists for anything else to reference.
 *
 * A share link's password, when the caller sets one, travels here as plain
 * text — the server hashes it before it ever reaches a column, the same as
 * every other password in this codebase.
 */

export const FILE_STATUSES = [
  'PENDING',
  'SCANNING',
  'AVAILABLE',
  'QUARANTINED',
  'FAILED',
  'ARCHIVED',
] as const;
export type FileStatus = (typeof FILE_STATUSES)[number];

export const createUploadSessionRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(1).max(150),
  /** Bytes. Validated against the configured maximum before any storage call. */
  declaredSize: z.number().int().positive(),
  /** What this file will attach to, e.g. `Person`. Null for a standalone upload. */
  ownerType: z.string().trim().min(1).max(100).nullish(),
  ownerId: z.uuid().nullish(),
  /** Which Mi Nube folder this lands in. Null is the drive's root. */
  folderId: z.uuid().nullish(),
});
export type CreateUploadSessionRequest = z.infer<typeof createUploadSessionRequestSchema>;

/**
 * `UploadSession` persists only `objectKey`/`declaredSize`/`declaredMime` —
 * `fileName`, the owner reference and the folder are not columns on it, so
 * the caller resupplies them here rather than this service trying to
 * round-trip them out of the object key.
 */
export const completeUploadRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  ownerType: z.string().trim().min(1).max(100).nullish(),
  ownerId: z.uuid().nullish(),
  folderId: z.uuid().nullish(),
});
export type CompleteUploadRequest = z.infer<typeof completeUploadRequestSchema>;

export interface UploadSessionResponse {
  readonly id: string;
  /** PUT the file's bytes here directly — never through this API's JSON body. */
  readonly uploadUrl: string;
  readonly expiresAt: string;
}

export const listFilesQuerySchema = z.object({
  ownerType: z.string().nullish(),
  ownerId: z.uuid().nullish(),
  /** Absent or null means the drive's root; an id means that folder's
   *  contents. There is no "every file regardless of folder" mode — Mi
   *  Nube is always browsed one folder at a time. */
  folderId: z.uuid().nullish(),
  includeTrashed: z.coerce.boolean().default(false),
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type ListFilesQuery = z.infer<typeof listFilesQuerySchema>;

export const moveFileRequestSchema = z.object({
  folderId: z.uuid().nullish(),
});
export type MoveFileRequest = z.infer<typeof moveFileRequestSchema>;

export interface FileObjectResponse {
  readonly id: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly status: FileStatus;
  readonly ownerType: string | null;
  readonly ownerId: string | null;
  readonly folderId: string | null;
  readonly createdAt: string;
  /** Doubles as "in the trash" — see the Prisma model's own comment. */
  readonly archivedAt: string | null;
  readonly shareCount: number;
}

export interface DownloadUrlResponse {
  readonly url: string;
  readonly expiresAt: string;
}

// --- Folders ("Mi Nube") -----------------------------------------------------

export const createFolderRequestSchema = z.object({
  name: z.string().trim().min(1).max(255),
  parentFolderId: z.uuid().nullish(),
});
export type CreateFolderRequest = z.infer<typeof createFolderRequestSchema>;

export const updateFolderRequestSchema = z.object({
  name: z.string().trim().min(1).max(255),
});
export type UpdateFolderRequest = z.infer<typeof updateFolderRequestSchema>;

export const listFoldersQuerySchema = z.object({
  parentFolderId: z.uuid().nullish(),
  includeTrashed: z.coerce.boolean().default(false),
});
export type ListFoldersQuery = z.infer<typeof listFoldersQuerySchema>;

export interface FolderSummaryResponse {
  readonly id: string;
  readonly name: string;
  readonly parentFolderId: string | null;
  readonly folderCount: number;
  readonly fileCount: number;
  readonly trashedAt: string | null;
}

/** `path` is the chain of ancestors root-first, for the breadcrumb — the
 *  same reason `GroupDetailResponse.parentGroupName` is resolved server
 *  side rather than making the client walk `parentFolderId` itself. */
export interface FolderDetailResponse extends FolderSummaryResponse {
  readonly path: readonly { readonly id: string; readonly name: string }[];
  readonly createdAt: string;
}

// --- Share links ---------------------------------------------------------------

export const FILE_SHARE_VISIBILITIES = ['ANYONE_WITH_LINK', 'SPECIFIC_PEOPLE'] as const;
export type FileShareVisibility = (typeof FILE_SHARE_VISIBILITIES)[number];

/**
 * `expiresInSeconds` rather than a client-computed `expiresAt`: the
 * "1 hour / 1 day / 1 week / forever" choice is a duration from *now*, and
 * having the server add the clock keeps a client's wrong wall-clock from
 * producing a link that expires in the past. `null` means never.
 */
export const createShareRequestSchema = z
  .object({
    visibility: z.enum(FILE_SHARE_VISIBILITIES),
    password: z.string().min(4).max(200).nullish(),
    expiresInSeconds: z.number().int().positive().nullish(),
    /** Required, non-empty, when `visibility` is `SPECIFIC_PEOPLE`. */
    recipientPersonIds: z.array(z.uuid()).max(100).nullish(),
  })
  .refine(
    (value) =>
      value.visibility !== 'SPECIFIC_PEOPLE' ||
      (value.recipientPersonIds && value.recipientPersonIds.length > 0),
    {
      message: 'At least one recipient is required for a restricted share',
      path: ['recipientPersonIds'],
    },
  );
export type CreateShareRequest = z.infer<typeof createShareRequestSchema>;

export const unlockShareRequestSchema = z.object({
  password: z.string().nullish(),
});
export type UnlockShareRequest = z.infer<typeof unlockShareRequestSchema>;

export interface FileShareRecipientResponse {
  readonly personId: string;
  readonly displayName: string;
}

export interface FileShareResponse {
  readonly id: string;
  readonly fileId: string;
  readonly token: string;
  /** The full share URL, ready to copy — built server-side so the client
   *  never has to know its own origin plus the token's path shape. */
  readonly url: string;
  readonly visibility: FileShareVisibility;
  readonly hasPassword: boolean;
  readonly expiresAt: string | null;
  readonly revokedAt: string | null;
  readonly recipients: readonly FileShareRecipientResponse[];
  readonly createdAt: string;
}

/**
 * What the public `/files/shares/:token` GET returns — enough to render the
 * unlock screen, never the download URL itself. That only comes back from
 * `unlock`, after the password (if any) and the signed-in identity (if
 * restricted) both check out.
 */
export interface PublicShareInfoResponse {
  readonly fileName: string;
  readonly sizeBytes: number;
  readonly mimeType: string;
  readonly requiresPassword: boolean;
  /** True when `visibility` is `SPECIFIC_PEOPLE` — the caller must be
   *  signed in as one of the share's recipients before `unlock` succeeds. */
  readonly requiresSignIn: boolean;
}
